import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { TrendingUp, TrendingDown, AlertCircle } from 'lucide-react';

const ViolationPatternChart = ({ patterns }) => {
  const chartData = patterns?.slice(0, 8)?.map(pattern => ({
    name: pattern?.patternName?.substring(0, 30) + '...',
    frequency: pattern?.occurrenceFrequency,
    preventionCost: pattern?.estimatedPreventionCostKina,
    violationCost: pattern?.estimatedViolationCostKina,
    roi: pattern?.roiPercentage
  }));

  const getTriggerTypeLabel = (type) => {
    const labels = {
      minimum_wage_breach: 'Minimum Wage',
      overtime_rate_violation: 'Overtime Rate',
      leave_entitlement_breach: 'Leave Entitlement',
      contract_compliance_violation: 'Contract Compliance',
      nasfund_calculation_error: 'NASFUND Calculation',
      swt_calculation_error: 'SWT Calculation',
      working_hours_violation: 'Working Hours',
      payment_delay_violation: 'Payment Delay'
    };
    return labels?.[type] || type;
  };

  return (
    <div className="space-y-6">
      {/* Pattern Frequency Chart */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-4">Violation Occurrence Frequency</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="name" tick={{ fontSize: 12 }} angle={-45} textAnchor="end" height={100} />
            <YAxis tick={{ fontSize: 12 }} />
            <Tooltip
              contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
            />
            <Legend />
            <Bar dataKey="frequency" fill="#ef4444" name="Occurrences" radius={[8, 8, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Common Violation Triggers */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-4">Common Violation Triggers</h3>
        <div className="space-y-3">
          {patterns?.slice(0, 5)?.map((pattern, index) => (
            <div key={index} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
              <AlertCircle className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-gray-900">
                    {getTriggerTypeLabel(pattern?.violationTriggerType)}
                  </span>
                  <span className="text-xs font-semibold text-orange-600">
                    {pattern?.occurrenceFrequency} occurrences
                  </span>
                </div>
                <p className="text-xs text-gray-600 mb-2">{pattern?.patternName}</p>
                <div className="flex items-center gap-4 text-xs">
                  <span className="text-gray-500">
                    Departments: {pattern?.affectedDepartments?.join(', ') || 'N/A'}
                  </span>
                  {pattern?.trendDirection && (
                    <span className={`flex items-center gap-1 ${
                      pattern?.trendDirection === 'increasing' ? 'text-red-600' : 'text-green-600'
                    }`}>
                      {pattern?.trendDirection === 'increasing' ? (
                        <TrendingUp className="h-3 w-3" />
                      ) : (
                        <TrendingDown className="h-3 w-3" />
                      )}
                      {pattern?.trendDirection}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Prevention Recommendations */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-4">Automated Prevention Recommendations</h3>
        <div className="space-y-2">
          {patterns?.slice(0, 3)?.map((pattern, index) => (
            <div key={index} className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">
                  {index + 1}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900 mb-1">{pattern?.preventionStrategy}</p>
                  {pattern?.automationOpportunity && (
                    <p className="text-xs text-gray-600">{pattern?.automationOpportunity}</p>
                  )}
                  <div className="mt-2 flex items-center gap-4 text-xs">
                    <span className="text-gray-600">
                      Prevention Cost: <span className="font-semibold text-gray-900">K{pattern?.estimatedPreventionCostKina?.toLocaleString()}</span>
                    </span>
                    <span className="text-green-600 font-semibold">
                      ROI: {pattern?.roiPercentage?.toFixed(0)}%
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ViolationPatternChart;